/**
 * Sections API - Update Section Attendance
 *
 * POST /v1/sections/:section_id/attendance/update
 *
 * Mirrors apps/backend/src/api/sections/v1/use-cases/attendance/update-attendance/schema.ts.
 *
 * Two modes share one endpoint + resolution path:
 *  - `dry_run: true` (the web tester default) resolves the full payload and
 *    echoes it back WITHOUT committing a Blackbaud record.
 *  - real write (`dry_run` false/omitted) commits to Blackbaud and echoes the
 *    same resolved payload alongside `students_updated`.
 *
 * `meeting_start_time` is the client's LOOKUP KEY for the meeting: the
 * school-local wall clock, sent without a zone designator. On the teacher path
 * the backend uses it only to find the meeting in Blackbaud's schedule, then
 * sends Blackbaud that meeting's own `start_time` verbatim, offset included
 * (SCH-1660 KTD1). The client never adds an offset: the one it knows is the
 * device's zone, not the school's.
 */

import { z } from 'zod'

/**
 * One student row. Only students whose record should change are listed; an
 * omitted student is left as Blackbaud has it.
 *
 * `excuse_type_id` is REQUIRED (SCH-1660 KTD3). A missing excuse type used to be
 * read as Present, so a row that lost its reason recorded the opposite of the
 * teacher's mark. A Present row names the school's Present excuse type
 * explicitly instead.
 */
export const StudentAttendanceUpdateRequestSchema = z.object({
  student_user_id: z.string().optional().describe('Student user public ID (CUID from usersOnSchools.publicId)'),
  student_platform_id: z.number().int().positive().optional().describe('Student Blackbaud platform ID (integer)'),
  excuse_type_id: z.string().describe('Excuse type public ID (CUID from z_RefAttendanceExcusedTypes.publicId)'),
  comment: z.string().max(500).optional().describe('Optional comment for attendance record')
})

export type StudentAttendanceUpdateRequest = z.infer<typeof StudentAttendanceUpdateRequestSchema>

export const UpdateSectionAttendanceRequestSchema = z.object({
  meeting_start_time: z
    .string()
    .describe(
      'Meeting lookup key: ISO-8601 school-local wall clock, no Z/offset (the backend sends BB its own start_time)'
    ),
  mark_class_canceled: z.boolean().optional().describe('Mark the class as canceled'),
  reported_by: z.number().int().positive().optional().describe('Blackbaud user ID of who reported attendance'),
  student_attendances: z
    .array(StudentAttendanceUpdateRequestSchema)
    .optional()
    .describe('Students whose record should change. Students not listed are left as Blackbaud has them.'),
  dry_run: z.boolean().optional().describe('When true, resolve and echo the payload without writing to Blackbaud'),
  // Telemetry counts (SCH-1660 KTD7): with touched-only payloads they separate
  // an all-present save from a save that lost its rows.
  roster_size: z.number().int().min(0).optional().describe('Students on the roster the teacher saw'),
  override_count: z.number().int().min(0).optional().describe('Rows that override an existing Blackbaud record')
})

export type UpdateSectionAttendanceRequest = z.infer<typeof UpdateSectionAttendanceRequestSchema>

/** Resolved exception-student echo (BB platform ids). */
export const ResolvedStudentAttendanceSchema = z.object({
  student_platform_id: z.number().int().describe('Resolved student Blackbaud platform ID'),
  excuse_type_id: z.number().int().nullable().describe('Resolved excuse type Blackbaud platform ID (null = present)'),
  comment: z.string().nullable().describe('Optional comment for the attendance record')
})

export type ResolvedStudentAttendance = z.infer<typeof ResolvedStudentAttendanceSchema>

/** The fully-resolved payload echoed in BOTH dry-run and real-write modes. */
export const ResolvedAttendancePayloadSchema = z.object({
  section_platform_id: z.string().describe('Resolved section Blackbaud platform ID'),
  meeting_start_time: z.string().describe('Resolved meeting start time (verbatim, school-local)'),
  reported_by: z.number().int().nullable().describe('Resolved reporter Blackbaud platform ID (null = calling user)'),
  mark_class_canceled: z.boolean().describe('Whether the class is marked canceled'),
  student_attendances: z
    .array(ResolvedStudentAttendanceSchema)
    .describe('Resolved exception students (present students omitted per BB semantics)')
})

export type ResolvedAttendancePayload = z.infer<typeof ResolvedAttendancePayloadSchema>

export const UpdateSectionAttendanceDataSchema = z.object({
  success: z.boolean(),
  message: z.string(),
  section_id: z.string(),
  section_platform_id: z.string(),
  students_updated: z.number().int().min(0),
  meeting_start_time: z.string(),
  class_canceled: z.boolean(),
  dry_run: z.boolean().describe('Whether this was a dry-run (no Blackbaud write)'),
  resolved: ResolvedAttendancePayloadSchema.optional().describe('The fully-resolved payload (echoed in both modes)'),
  // SCH-1660 R21: a 200 from Blackbaud is not proof of a write. Optional here
  // although the backend always sends it: the web client parses this schema at
  // runtime, and a backend that predates the field must not fail that parse.
  // Treat an absent value as `succeeded`.
  outcome: z
    .enum(['succeeded', 'succeeded_unverified'])
    .optional()
    .describe('succeeded_unverified: Blackbaud accepted the write but the read-back did not confirm it')
})

export type UpdateSectionAttendanceData = z.infer<typeof UpdateSectionAttendanceDataSchema>

export const UpdateSectionAttendanceResponseSchema = z.object({
  message: z.string(),
  data: UpdateSectionAttendanceDataSchema
})

export type UpdateSectionAttendanceResponse = z.infer<typeof UpdateSectionAttendanceResponseSchema>

/**
 * The two rejections a Present OVERRIDE can earn (SCH-1660 R17), as the API
 * error `type` the client receives with a 400 (bare on the wire; see
 * `UPDATE_SECTION_ATTENDANCE_BARE_CODES`).
 *
 * Why the server checks at all: an explicit Present DELETES whatever exception
 * the office already had on that meeting (finding D4). It is the one row on this
 * path that destroys another author's work, so the server refuses to send one it
 * cannot see a target for — and refuses the WHOLE submission rather than
 * dropping the row, because a half-written roster is worse than a retry.
 *
 * - `OVERRIDE_TARGET_MISSING` — the meeting's current Blackbaud records hold no
 *   exception for that student. There is nothing to override; a Present would
 *   only risk deleting something that arrived between the read and the write.
 * - `OVERRIDE_TARGET_UNCHECKED` — that student was never covered by the read
 *   (no school level, or no Blackbaud id), so the server cannot vouch that the
 *   record is clean. "Not checked" is not "clean" (R16).
 *
 * Nothing is written for either: both run before the first Blackbaud POST. The
 * teacher's other marks are untouched, so a client keeps the marked roster and
 * asks the teacher to clear the offending Present rather than discarding it.
 */
export const ATTENDANCE_OVERRIDE_REJECTION_CODES = ['OVERRIDE_TARGET_MISSING', 'OVERRIDE_TARGET_UNCHECKED'] as const

export type AttendanceOverrideRejectionCode = (typeof ATTENDANCE_OVERRIDE_REJECTION_CODES)[number]

/**
 * The two reason refusals (SCH-1660 U6): the school's reason policy moved
 * under the teacher mid-session.
 *
 * - `REASON_NOT_ALLOWED` — the chosen reason was retired in Blackbaud or dropped
 *   from the school's allow-list.
 * - `REASON_NOT_ALLOWED_AT_LEVEL` — the reason is not offered at that student's
 *   school level.
 *
 * Both run before the first Blackbaud call, so nothing was written. A client
 * refetches the reason config and keeps the teacher's marks.
 */
export const ATTENDANCE_REASON_REFUSAL_CODES = ['REASON_NOT_ALLOWED', 'REASON_NOT_ALLOWED_AT_LEVEL'] as const

export type AttendanceReasonRefusalCode = (typeof ATTENDANCE_REASON_REFUSAL_CODES)[number]

/**
 * The exact wire codes `POST /v1/sections/:section_id/attendance/update` sends
 * WITHOUT its `ATTENDANCE_` prefix: the reason refusals plus the override
 * rejections, each with a 400. Every other code on the endpoint is prefixed
 * (`ATTENDANCE_SECTION_NOT_FOUND`, `ATTENDANCE_WRITE_NOT_ENABLED`, ...).
 *
 * Why bare (SCH-3484): the app shipped comparing these four codes bare
 * (SCH-1660), while the server prefixed them, so the recovery branches never
 * fired. There is no minimum-version gate, so installed builds keep that bare
 * comparison for as long as they are installed; the server sends the form they
 * read. The matchers below accept both forms anyway, so a later namespace
 * change cannot re-break a build that uses them.
 */
export const UPDATE_SECTION_ATTENDANCE_BARE_CODES = [
  ...ATTENDANCE_REASON_REFUSAL_CODES,
  ...ATTENDANCE_OVERRIDE_REJECTION_CODES
] as const

export type AttendanceRefusalCode = (typeof UPDATE_SECTION_ATTENDANCE_BARE_CODES)[number]

/** The update endpoint's `apiErrorPrefix`, applied to every code not listed above. */
const UPDATE_SECTION_ATTENDANCE_PREFIX = 'ATTENDANCE_'

/**
 * The domain code of an attendance refusal, from either wire form: bare
 * (`REASON_NOT_ALLOWED`, what the server sends) or prefixed
 * (`ATTENDANCE_REASON_NOT_ALLOWED`, what it sent before SCH-3484). Undefined
 * for any other code, and for a missing one.
 */
export function attendanceRefusalCode(wireType: string | null | undefined): AttendanceRefusalCode | undefined {
  if (typeof wireType !== 'string') return undefined
  const domainType = wireType.startsWith(UPDATE_SECTION_ATTENDANCE_PREFIX)
    ? wireType.slice(UPDATE_SECTION_ATTENDANCE_PREFIX.length)
    : wireType
  return UPDATE_SECTION_ATTENDANCE_BARE_CODES.find(code => code === domainType)
}

/** Whether an API error type, bare or `ATTENDANCE_`-prefixed, is one of the reason refusals above. */
export function isAttendanceReasonRefusal(wireType: string | null | undefined): boolean {
  const code = attendanceRefusalCode(wireType)
  return code !== undefined && (ATTENDANCE_REASON_REFUSAL_CODES as readonly string[]).includes(code)
}

/** Whether an API error type, bare or `ATTENDANCE_`-prefixed, is one of the override rejections above. */
export function isAttendanceOverrideRejection(errorType: string | null | undefined): boolean {
  const code = attendanceRefusalCode(errorType)
  return code !== undefined && (ATTENDANCE_OVERRIDE_REJECTION_CODES as readonly string[]).includes(code)
}

// ---------------------------------------------------------------------------
// Prefill read — GET /v1/sections/:section_id/attendance?meeting_start_time=
//
// What Blackbaud ALREADY holds for one meeting, so the roster can show the
// office's marks before a teacher touches anything (SCH-1660 KTD6, R13).
// Mirrors apps/backend/src/api/sections/v1/use-cases/attendance/get-meeting-attendance/schema.ts.
// ---------------------------------------------------------------------------

export const GetMeetingAttendanceParamsSchema = z.object({
  section_id: z.string().describe('Section public ID (CUID)')
})

export type GetMeetingAttendanceParams = z.infer<typeof GetMeetingAttendanceParamsSchema>

export const GetMeetingAttendanceQuerySchema = z.object({
  // The same lookup key the write takes: school-local wall clock, no offset.
  // The client never adds one — the offset it knows is the device's zone, not
  // the school's (KTD1).
  meeting_start_time: z.string().describe('Meeting lookup key: ISO-8601 school-local wall clock, no Z/offset')
})

export type GetMeetingAttendanceQuery = z.infer<typeof GetMeetingAttendanceQuerySchema>

/**
 * The office comment, parsed on the backend into allowlisted values (SCH-2219,
 * KTD5). Every value is an enum or a re-rendered time, never comment text (R13).
 * The app writes the sentence ("Late arrival · 8:57 AM · front desk").
 */
export const OfficeReasonSourceSchema = z.enum(['front_desk', 'conciergepad', 'parent_app', 'teacher_app'])
export type OfficeReasonSource = z.infer<typeof OfficeReasonSourceSchema>

export const OfficeReasonEventSchema = z.enum([
  'early_dismissal',
  'late_arrival',
  'illness',
  'family_emergency',
  'fever'
])
export type OfficeReasonEvent = z.infer<typeof OfficeReasonEventSchema>

export const OfficeReasonSchema = z.object({
  source: OfficeReasonSourceSchema.describe('Who recorded the mark, as far as the comment shows'),
  event: OfficeReasonEventSchema.nullable().describe('The recognised event, or null'),
  time: z.string().nullable().describe('Kiosk arrival/departure time as h:mm AM; only for front_desk, otherwise null')
})

export type OfficeReason = z.infer<typeof OfficeReasonSchema>

/** One mark the office (or a kiosk) already recorded on this meeting. */
export const MeetingAttendanceMarkSchema = z.object({
  student_public_id: z.string().describe('Student UsersOnSchools public ID'),
  excuse_type_id: z
    .string()
    .nullable()
    .describe('Excuse type public ID (CUID), or null when the school mirror holds no row for it'),
  label: z.string().nullable().describe('Human-readable reason'),
  attendance_type: z.string().nullable().describe('Present / Absence / Tardy, from the school mirror'),
  // R13: the office's comment TEXT never leaves the backend. The row shows that
  // a note exists, not what it says.
  has_note: z.boolean().describe('Whether a record comment exists; the text is never returned'),
  // Nullable AND optional (KTD6): an older backend omits it, and the app then
  // shows the label plus "Has a note".
  office_reason: OfficeReasonSchema.nullable()
    .optional()
    .describe('The comment parsed into allowlisted values, or null when unrecognised')
})

export type MeetingAttendanceMark = z.infer<typeof MeetingAttendanceMarkSchema>

export const MeetingAttendanceMeetingSchema = z.object({
  start_time: z.string().describe("Blackbaud's own offset-bearing start_time for the matched meeting"),
  // Not a "records exist" signal: office and kiosk marks sit on meetings that
  // read false, which is the case prefill exists for (R3, V4).
  attendance_taken: z.boolean().describe('Whether a teacher has run a session on this meeting'),
  num_absent: z.number().int().min(0).describe("Blackbaud's own absence count for the meeting")
})

export type MeetingAttendanceMeeting = z.infer<typeof MeetingAttendanceMeetingSchema>

export const GetMeetingAttendanceDataSchema = z.object({
  meeting: MeetingAttendanceMeetingSchema,
  marks: z.array(MeetingAttendanceMarkSchema).describe('Existing Blackbaud marks, pending rows excluded (R18)'),
  // R16: `partial` is not "empty". A student the read could not cover is
  // unknown, never clean, and the app badges them "Not checked".
  partial: z.boolean().describe('True when at least one roster student could not be checked'),
  unchecked_student_public_ids: z
    .array(z.string())
    .describe('The students behind `partial`; empty when partial is false')
})

export type GetMeetingAttendanceData = z.infer<typeof GetMeetingAttendanceDataSchema>

export const GetMeetingAttendanceResponseSchema = z.object({
  message: z.string(),
  data: GetMeetingAttendanceDataSchema
})

export type GetMeetingAttendanceResponse = z.infer<typeof GetMeetingAttendanceResponseSchema>
